import React, { useState } from 'react'
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { Activity } from '../../types'
import { getDisplayDurationMinutes } from '../../utils/activityConfiguration'
import { CalendarPicker } from '../CalendarPicker/CalendarPicker'
import { LocalTimeBadge } from '../../../../../shared/components/LocalTimeBadge'

export interface ScheduledActivity extends Activity {
  scheduledDate?: string
  scheduledTime?: string
  endTime?: string
}

export interface ScheduleActivityItemProps {
  activity: ScheduledActivity
  index: number
  previousActivity?: ScheduledActivity
  onSchedule: (activityId: string, date: string, startTime: string, endTime: string) => void
  hasError?: boolean
  errorMessage?: string
  className?: string
}

export const ScheduleActivityItem: React.FC<ScheduleActivityItemProps> = ({
  activity,
  index,
  previousActivity,
  onSchedule,
  hasError = false,
  errorMessage,
  className = ''
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [isSelected, setIsSelected] = useState(false)

  const handleScheduleClick = () => {
    setIsCalendarOpen(true)
    setIsSelected(true)
  }

  const handleDateSelect = (date: string, startTime: string, endTime: string) => {
    onSchedule(activity.id, date, startTime, endTime)
    setIsCalendarOpen(false)
    setIsSelected(false)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }
    return date.toLocaleDateString('en-US', options)
  }

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':')
    const hour24 = parseInt(hours)
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    const ampm = hour24 >= 12 ? 'PM' : 'AM'
    return `${hour12}:${minutes} ${ampm}`
  }

  const IconComponent = activity.icon
  const displayDuration = getDisplayDurationMinutes(activity)

  return (
    <>
      <div
        className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
          isSelected ? 'ring-2 ring-blue-500' : ''
        } ${className}`}
        style={{
          backgroundColor: hasError ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.heritageBlue40,
          borderColor: hasError
            ? INTERAX_COLORS.redError
            : isSelected
              ? INTERAX_COLORS.sun
              : INTERAX_COLORS.heritageBlue
        }}
      >
        {/* Activity icon */}
        <div
          className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
          style={{ backgroundColor: INTERAX_COLORS.heritageBlue }}
        >
          <div style={{ color: INTERAX_COLORS.sun }}>
            <IconComponent className="w-6 h-6" />
          </div>
        </div>

        {/* Activity info */}
        <div className="flex-1 min-w-0">
          <h4
            className="font-semibold mb-1"
            style={{
              color: INTERAX_COLORS.white,
              fontFamily: INTERAX_TYPOGRAPHY.body1.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body1.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body1.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body1.lineHeight
            }}
          >
            {activity.title}
          </h4>
          <div className="flex items-center gap-2">
            <ClockIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.thunderGrey }} />
            <span
              className="text-sm"
              style={{
                color: INTERAX_COLORS.thunderGrey,
                fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
              }}
            >
              {displayDuration} min
            </span>
          </div>
        </div>

        {/* Schedule button or scheduled info */}
        <div className="flex-shrink-0">
          {activity.scheduledDate && activity.scheduledTime && activity.endTime ? (
            <button
              onClick={handleScheduleClick}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors border-2 hover:opacity-80"
              data-testid={`schedule-activity-${index - 1}`}
              style={{
                backgroundColor: 'transparent',
                borderColor: INTERAX_COLORS.sun,
                color: INTERAX_COLORS.sun,
                fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
              }}
            >
              <CalendarIcon className="w-4 h-4" />
              <span className="whitespace-nowrap">
                {formatDate(activity.scheduledDate)} • {formatTime(activity.scheduledTime)} –{' '}
                {formatTime(activity.endTime)}
              </span>
              <LocalTimeBadge variant="scheduling" />
            </button>
          ) : (
            <button
              onClick={handleScheduleClick}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors"
              data-testid={`schedule-activity-${index - 1}`}
              style={{
                backgroundColor: INTERAX_COLORS.sun,
                color: INTERAX_COLORS.offBlack,
                fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
              }}
            >
              <CalendarIcon className="w-4 h-4" />
              Schedule activity
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {hasError && errorMessage && (
        <div
          className="mt-2 px-4 py-2 rounded-lg flex items-start gap-2"
          style={{
            backgroundColor: INTERAX_COLORS.redErrorBackground,
            borderLeft: `3px solid ${INTERAX_COLORS.redError}`
          }}
        >
          <span
            className="text-sm"
            style={{
              color: INTERAX_COLORS.redError,
              fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
            }}
          >
            ⚠️ {errorMessage}
          </span>
        </div>
      )}

      {/* Calendar picker modal */}
      <CalendarPicker
        isOpen={isCalendarOpen}
        onClose={() => {
          setIsCalendarOpen(false)
          setIsSelected(false)
        }}
        onDateSelect={handleDateSelect}
        activityDuration={displayDuration}
        initialDate={activity.scheduledDate}
        initialTime={activity.scheduledTime}
        previousActivity={previousActivity}
      />
    </>
  )
}
